// Loads a Google font on demand.
//
// The font list is long, and shipping every family in index.html would
// mean downloading dozens of faces on every page load for the sake of one
// dropdown. Instead the stylesheet is injected the first time a family is
// actually selected, and only once.

// Families that must never be fetched from the CDN by this loader —
// either already requested by index.html (the UI faces) or self-hosted in
// src/fonts.css (the three creative-template render faces). Anything
// injected below is added here too, so each family is requested once.
const loaded = new Set<string>([
  // UI faces, from the index.html <link>.
  'Archivo',
  'Inter',
  'Poppins',
  // Render faces, self-hosted. Injecting a CDN <link> for these would
  // reintroduce exactly the dependency fonts.css exists to remove.
  'Geist',
  'Geist Mono',
  'Jost',
]);

/** Pull the family name out of a CSS font stack: "'DM Sans', sans-serif" → "DM Sans". */
export function familyOf(stack: string): string | null {
  const quoted = stack.match(/^\s*['"]([^'"]+)['"]/);
  if (quoted) return quoted[1] ?? null;
  // Unquoted first token — a system face like `Georgia, serif`.
  const bare = stack.split(',')[0]?.trim();
  return bare || null;
}

/**
 * Ensure the family behind `stack` is available, injecting the Google
 * Fonts stylesheet if needed. Safe to call repeatedly and on every render.
 * System faces (no `google: true`) are skipped — they need no fetch.
 */
export function ensureGoogleFont(stack: string, google = true): void {
  if (!google || typeof document === 'undefined') return;
  const family = familyOf(stack);
  if (!family || loaded.has(family)) return;
  loaded.add(family);

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  // Weights match what the templates ask for (400–700).
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
    family,
  ).replace(/%20/g, '+')}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}
